/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Form } from 'antd';

import { Node } from '@/client';
import { IncomingData } from '@/components/Node/IncomingData';
import { NodeViewPageCard } from '@/components/Node/NodeViewPageCard';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Col } from '@/primitives/Col';
import { Row } from '@/primitives/Row';

interface NodeServerConfigProps {
  node: Node;
  loading: boolean;
}

export function NodeServerConfig({ node, loading }: NodeServerConfigProps) {
  const { t } = useMimicTranslation('node');
  const { t: tGlobal } = useMimicTranslation('global');

  const operatingSystemName = node.systemProfile?.['os.name'] || undefined;
  const operatingSystemVersion = node.systemProfile?.['os.version'] || undefined;
  const operatingSystemDescription = operatingSystemName
    ? `${operatingSystemName} ${operatingSystemVersion}`.trim()
    : tGlobal('notAvailable');

  const cpuName = node.systemProfile?.['host.cpu.model.name']?.replace('(R)', '®').replace('(TM)', '™');

  return (
    <NodeViewPageCard title={t('server_configuration')}>
      <Form layout="vertical">
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <IncomingData
              label={t('ip_address')}
              loading={loading}
              text={node.systemProfile?.['host.ip']}
              dataTestId="node-ip-address"
              mono
              copyable
            />
          </Col>
          <Col span={12}>
            <IncomingData
              dataTestId="node-operating-system"
              label={t('os')}
              loading={loading}
              text={operatingSystemDescription}
              mono
              copyable
            />
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <IncomingData
              label={t('cpuModelName')}
              loading={loading}
              text={cpuName}
              dataTestId="node-cpu-nodel-name"
              mono
              copyable
            />
          </Col>
          <Col span={12}>
            <IncomingData
              label={t('cpuArchitecture')}
              loading={loading}
              text={node.systemProfile?.['host.arch']}
              dataTestId="node-cpu-architecture"
              mono
              copyable
            />
          </Col>
        </Row>
        {(node.systemProfile?.['host.windows_domain'] || node.systemProfile?.['host.windows_workgroup']) && (
          <Row gutter={[16, 16]}>
            {node.systemProfile?.['host.windows_domain'] && (
              <Col span={12}>
                <IncomingData
                  label={t('windows_domain')}
                  loading={loading}
                  text={node.systemProfile['host.windows_domain']}
                  mono
                  copyable
                />
              </Col>
            )}
            {node.systemProfile?.['host.windows_workgroup'] && (
              <Col span={12}>
                <IncomingData
                  label={t('windows_workgroup')}
                  loading={loading}
                  text={node.systemProfile['host.windows_workgroup']}
                  mono
                  copyable
                />
              </Col>
            )}
          </Row>
        )}
      </Form>
    </NodeViewPageCard>
  );
}
